import SampleProductBudget from "@gaiaherbs/Components/SampleProductBudget";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@gaiaherbs/Overrides/Components/ProductPartNumbers";
import CartLineQuantity, { CartLineQuantityStyles } from "@gaiaherbs/Overrides/Widgets/Cart/CartLineQuantity";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import translate from "@insite/client-framework/Translate";
import { PromotionModel } from "@insite/client-framework/Types/ApiModels";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import CartLineNotes, { CartLineNotesStyles } from "@insite/content-library/Widgets/Cart/CartLineNotes";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Radio, { RadioProps, RadioStyle } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupProps } from "@insite/mobius/RadioGroup";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    promotions?: PromotionModel[];
    showSavingsAmount: boolean;
    showSavingsPercent: boolean;
    editable: boolean;
    showInvalidPriceMessage?: boolean;
    extendedStyles?: CartLineCardExpandedStyles;
}

const mapStateToProps = (state: ApplicationState) => ({
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    userType: state.context.session.properties?.userType,
    isEnabledCarstock: state.context.settings.properties?.isCarStockEnabled,
    showPricing: state.context.showPricing,
    isCheckoutReviewAndSubmitPage: getCurrentPage(state).type === "CheckoutReviewAndSubmitPage",
});

const mapDispatchToProps = {
    updateCartLine,
};

type Props = OwnProps &
    HasCartLineContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface CartLineCardExpandedStyles {
    cartLineNotes?: CartLineNotesStyles;
    price?: ProductPriceStyles;
    quantity?: CartLineQuantityStyles;
    container?: GridContainerProps;
    productImageGridItem?: GridItemProps;
    productImage?: ProductImageStyles;
    orderLineInfoGridItem?: GridItemProps;
    orderLineInfoContainer?: GridContainerProps;
    productDescriptionAndPartNumbersGridItem?: GridItemProps;
    productDescriptionAndPartNumbersContainer?: GridContainerProps;
    productBrand?: ProductBrandStyles;
    productBrandAndDescriptionGridItem?: GridItemProps;
    productDescription?: ProductDescriptionStyles;
    configurationGridItem?: GridItemProps;
    configurationOptionText?: TypographyProps;
    productPartNumbersGridItem?: GridItemProps;
    productPartNumbers?: ProductPartNumbersStyles;
    orderLineNotesGridItem?: GridItemProps;
    notesHeadingAndText?: SmallHeadingAndTextStyles;
    costCodeGridItem?: GridItemProps;
    costCodeValueText?: TypographyPresentationProps;
    costCodeLabelText?: TypographyPresentationProps;
    productPriceAndQuantityGridItem?: GridItemProps;
    productMarkAsFreeGridItem?: GridItemProps;
    productPriceAndQuantityContainer?: GridContainerProps;
    productMarkAsFreeContainer?: GridContainerProps;
    priceGridItem?: GridItemProps;
    MarkAsFreeButtonContainer?: GridItemProps;
    MarkAsFreeButtonGridItem?: GridItemProps;
    MarkAsFreeDropDownGridItem?: GridItemProps;
    priceHeadingAndText?: SmallHeadingAndTextStyles;
    promotionNameText?: TypographyProps;
    quantityGridItem?: GridItemProps;
    qtyHeadingAndText?: SmallHeadingAndTextStyles;
    extendedUnitNetPriceGridItem?: GridItemProps;
    extendedUnitNetPrice?: SmallHeadingAndTextStyles;
    subtotalWithoutVatText?: SmallHeadingAndTextStyles;
    vatAmount?: SmallHeadingAndTextStyles;
    cartLineErrorMessageGridItem?: GridItemProps;
    cartLineErrorMessageText?: TypographyProps;
    markAsFreeButton?: ButtonPresentationProps;
    unmarkAsFreeButton?: ButtonPresentationProps;
    selectReasonModal?: ModalPresentationProps;
    reasonListOptionRadioGroup?: RadioGroupProps;
    unmarkButtonWarpper?: InjectableCss;
    saveButton?: ButtonPresentationProps;
    cancelButton?: ButtonPresentationProps;
    reasonButtonWarpper?: InjectableCss;
}

export const cartLineCardExpandedStyles: CartLineCardExpandedStyles = {
    container: {
        gap: 20,
        offsetCss: css`
            max-width: 100%;
            margin: 0px;
        `,
        css: css`
            border: 1px solid ${getColor("common.border")};
            border-radius: 4px;
            margin: 1rem 0rem;
            padding: 1rem 0;
        `,
    },
    productImageGridItem: {
        width: 2,
        printWidth: 2,
        css: css`
            @media print {
                height: 200px;
                width: 200px;
                padding: 0;
            }
        `,
    },
    orderLineInfoGridItem: {
        width: 12,
        printWidth: 10,
    },
    orderLineInfoContainer: {
        gap: 15,
    },
    productDescriptionAndPartNumbersGridItem: {
        width: [12, 12, 4, 4, 4],
        printWidth: 6,
    },
    productDescriptionAndPartNumbersContainer: {
        gap: 15,
    },
    productDescription: {
        productDetailDescription: {
            variant: "h2",
            color: "primary",
            fontFamily: "LibreBaskerville",
            weight: "normal",
            size: 22,
            css: css`
                margin-bottom: 0px;
            `,
        },
    },
    productBrand: {
        nameText: {
            color: "primary",
        },
    },
    productBrandAndDescriptionGridItem: {
        css: css`
            flex-direction: column;
        `,
        width: 12,
    },
    configurationGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    productPartNumbersGridItem: {
        width: 12,
    },
    productPartNumbers: {
        erpNumberLabelText: {
            weight: "bold",
            fontFamily: "LatoBold",
            size: 14,
            css: css`
                flex: 0 0 auto;
                margin-right: 3px;
                margin-top: 1px;
                @media print {
                    font-weight: lighter !important;
                }
            `,
        },
        erpNumberValueText: {
            size: 14,
            fontFamily: "Lato",
            css: css`
                flex: 1 1 auto;
                min-width: 0;
                ${wrapInContainerStyles}
            `,
        },
    },
    orderLineNotesGridItem: {
        width: 12,
    },
    costCodeGridItem: { width: 12 },
    costCodeLabelText: {
        weight: "bold",
        css: css`
            margin-right: 10px;
        `,
    },
    productPriceAndQuantityGridItem: {
        width: [12, 12, 5, 5, 5],
        printWidth: 6,
    },
    productPriceAndQuantityContainer: {
        gap: 15,
    },
    productMarkAsFreeGridItem: {
        width: [12, 12, 3, 3, 3],
        printWidth: 6,
    },
    productMarkAsFreeContainer: {
        gap: 15,
    },
    MarkAsFreeButtonContainer: {
        css: css`
            height: 100%;
            display: flex;
            align-items: center;
            > ${GridOffset} {
                width: 100%;
                margin: 0;
            }
        `,
    },
    MarkAsFreeButtonGridItem: {
        width: [12, 12, 12, 12, 12],
        printWidth: 12,
        css: css`
            padding: 2px;
            margin-bottom: 15px;
            justify-content: center;
            > button[class^="ButtonWrapper"] {
                width: 178px !important;
            }
        `,
    },
    MarkAsFreeDropDownGridItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            padding: 2px;
            justify-content: center;
            .reason {
                width: 131px !important;
                height: 28px !important;
            }
            > div[class^="FormFieldStyle"] {
                display: flex;
                flex-direction: row;
                justify-content: center;
                > div[class^="FormInputWrapper"] {
                    width: 131px !important;
                    height: 28px !important;
                }
            }
        `,
    },
    markAsFreeButton: {
        variant: "secondary",
    },
    unmarkAsFreeButton: { variant: "tertiary" },
    priceGridItem: {
        width: [12, 12, 5, 5, 5],
        printWidth: 5,
        css: css`
            flex-direction: column;
        `,
    },
    cancelButton: {
        variant: "secondary",
    },
    saveButton: {},
    selectReasonModal: {
        sizeVariant: "small",
        cssOverrides: {
            modalBody: css`
                border-radius: 6px;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            border-radius: 0;
                        `,
                        css`
                            border-radius: 0;
                        `,
                        css`
                            border-radius: 0;
                        `,
                        null,
                        null,
                    ])}
            `,
            modalTitle: css`
                color: ${getColor("primary")};
                padding: 10px 15px;
                > h4 {
                    margin-bottom: 0;
                }
            `,
            modalContent: css`
                padding: 15px;
                padding-top: 5px;
            `,

            headlineTypography: css`
                font-size: 24px;
                font-family: LibreBaskerville;
            `,
        },
    },
    reasonListOptionRadioGroup: {
        css: css`
            flex-direction: column;
            & > ${RadioStyle} {
                margin-top: 10px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex-direction: column;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    unmarkButtonWarpper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;

            > span[class^="TypographyStyle"] {
                text-align: center;
            }
        `,
    },
    reasonButtonWarpper: {
        css: css`
            display: flex;
            justify-content: end;
            gap: 10px;
        `,
    },
    quantityGridItem: {
        width: [12, 12, 3, 3, 3],
    },
    extendedUnitNetPriceGridItem: {
        width: [12, 12, 4, 4, 4],
        css: css`
            flex-direction: column;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            flex-flow: wrap;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    extendedUnitNetPrice: {
        heading: {
            color: "text.main",
            size: 14,
            fontFamily: "Lato",
            css: css`
                text-transform: capitalize !important;
                font-weight: normal;
            `,
        },
        text: {
            size: 15,
            color: "primary",
            fontFamily: "LatoBold",
        },
    },
    price: {
        price: {
            priceText: {
                size: 15,
                color: "primary",
                fontFamily: "LatoBold",
                weight: "normal",
                css: css`
                    @media print {
                        font-size: 16px;
                    }
                `,
            },
            unitOfMeasureText: {
                size: 15,
                color: "primary",
                fontFamily: "LatoBold",
                css: css`
                    @media print {
                        font-size: 16px;
                    }
                `,
            },
        },
    },
    subtotalWithoutVatText: {
        wrapper: {
            css: css`
                margin-bottom: 6px;
                width: 100%;
                @media print {
                    font-size: 16px;
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                width: 50%;
                            `,
                        ],
                        "max",
                    )}
            `,
        },
    },
    vatAmount: {
        wrapper: {
            css: css`
                margin-bottom: 6px;
                width: 100%;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                width: 50%;
                            `,
                        ],
                        "max",
                    )}
            `,
        },
    },
    cartLineErrorMessageGridItem: {
        width: 12,
    },
    cartLineErrorMessageText: {
        color: "danger",
    },
};

const CartLineCardExpanded = ({
    cart,
    cartLine,
    updateCartLine,
    showSavingsAmount,
    showSavingsPercent,
    promotions,
    editable,
    enableVat,
    vatPriceDisplay,
    showInvalidPriceMessage,
    showPricing,
    isCheckoutReviewAndSubmitPage,
    extendedStyles,
    userType,
    isEnabledCarstock,
}: Props) => {
    const toasterContext = React.useContext(ToasterContext);
    const [styles] = React.useState(() => mergeToNew(cartLineCardExpandedStyles, extendedStyles));
    const [isMarkFree, setIsMarkFree] = React.useState(false);
    const [isShowReasonModal, setIsShowReasonModal] = React.useState(false);
    const [reasonListOption, setReasonListOption] = React.useState("");
    const [isReasonError, setIsReasonError] = React.useState(false);
    const isBroker = userType === "BROKER";

    const isCarStockItem = cartLine.properties?.isCarstockProduct === "true";

    const productQtyAllocatedToUser = cartLine.properties?.productQtyAllocatedToUser;

    const invoicedQty = cartLine.properties?.invoicedQty;
    // const productQtyAllocatedToUser = "20";

    const isOrderInCart = cart.status === "Cart";

    useEffect(() => {
        if (
            cartLine.properties?.isProductMarkedAsFreeForCarstock === "true" ||
            cartLine.properties?.isProductMarkedAsFreeForCarstock === "True"
        ) {
            setIsMarkFree(true);
            setReasonListOption(cartLine.notes);
        } else {
            setIsMarkFree(false);
            setReasonListOption("");
        }
    }, [cartLine]);

    const onHandleMarkAsFreeButton = () => {
        setReasonListOption("");
        setIsShowReasonModal(true);
    };

    const onHandleCloseModal = () => {
        setIsShowReasonModal(false);
        setIsReasonError(false);
    };

    const reasonListOptionChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setReasonListOption(event.currentTarget.value);
    };

    const onHandleUnMarkAsFreeButton = () => {
        updateCartLine({
            cartLine: {
                ...cartLine,
                notes: "",
                properties: {
                    ...cartLine.properties,
                    isProductMarkedAsFreeForCarstock: "false",
                },
            },
            onSuccess: () => {
                // toasterContext.addToast({
                //     body: translate("Line Notes Updated"),
                //     messageType: "success",
                // });
            },
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.();
                }
            },
        });
    };

    const submitMarkFreeHandler = () => {
        if (!reasonListOption) {
            setIsReasonError(true);
            return;
        }
        updateCartLine({
            cartLine: {
                ...cartLine,
                notes: reasonListOption,
                properties: {
                    ...cartLine.properties,
                    isProductMarkedAsFreeForCarstock: "true",
                },
            },
            onSuccess: () => {
                // toasterContext.addToast({
                //     body: translate("Apply successfully"),
                //     messageType: "success",
                // });
            },
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.();
                }
            },
        });
    };

    // Render reason options from site message
    const optionsString = siteMessage("Car_Stock_Reason") as string;
    const options = optionsString.split(",");

    return (
        <GridContainer
            {...styles.container}
            data-test-selector={`cartline_expanded_${cartLine.productId}_${cartLine.unitOfMeasure}`}
        >
            {/* <GridItem {...styles.productImageGridItem}>
                <ProductImage product={cartLine} extendedStyles={styles.productImage} />
            </GridItem> */}
            <GridItem {...styles.orderLineInfoGridItem}>
                <GridContainer {...styles.orderLineInfoContainer}>
                    <GridItem {...styles.productDescriptionAndPartNumbersGridItem}>
                        <GridContainer {...styles.productDescriptionAndPartNumbersContainer}>
                            <GridItem {...styles.productBrandAndDescriptionGridItem}>
                                {cartLine.brand && (
                                    <ProductBrand brand={cartLine.brand} extendedStyles={styles.productBrand} />
                                )}
                                <ProductDescription product={cartLine} extendedStyles={styles.productDescription} />
                            </GridItem>
                            {!cartLine.isFixedConfiguration && cartLine.sectionOptions!.length > 0 && (
                                <GridItem {...styles.configurationGridItem}>
                                    {cartLine.sectionOptions!.map(option => (
                                        <Typography {...styles.configurationOptionText} key={option.sectionOptionId}>
                                            {`${option.sectionName}:${option.optionName}`}
                                        </Typography>
                                    ))}
                                </GridItem>
                            )}
                            <GridItem {...styles.productPartNumbersGridItem}>
                                <ProductPartNumbers
                                    productNumber={cartLine.erpNumber}
                                    customerProductNumber={cartLine.customerName}
                                    upcNumber={cartLine.properties.upcCode}
                                    manufacturerItem={cartLine.manufacturerItem}
                                    extendedStyles={styles.productPartNumbers}
                                />
                            </GridItem>
                            {cartLine.isRestricted && (
                                <GridItem
                                    {...styles.cartLineErrorMessageGridItem}
                                    data-test-selector="cartline_restrictedMessage"
                                >
                                    <Typography {...styles.cartLineErrorMessageText}>
                                        {translate("Restricted product")}
                                    </Typography>
                                </GridItem>
                            )}
                            {!cartLine.isActive && (
                                <GridItem {...styles.cartLineErrorMessageGridItem}>
                                    <Typography {...styles.cartLineErrorMessageText}>
                                        {translate("Inactive Product")}
                                    </Typography>
                                </GridItem>
                            )}
                        </GridContainer>
                    </GridItem>
                    <GridItem {...styles.productPriceAndQuantityGridItem}>
                        <GridContainer {...styles.productPriceAndQuantityContainer}>
                            <GridItem {...styles.priceGridItem}>
                                <ProductPrice
                                    product={cartLine}
                                    showSavings={true}
                                    showSavingsAmount={showSavingsAmount}
                                    showSavingsPercent={showSavingsPercent}
                                    showInvalidPriceMessage={showInvalidPriceMessage}
                                    extendedStyles={styles.price}
                                />
                                {promotions?.map(promotion => (
                                    <Typography
                                        {...styles.promotionNameText}
                                        key={promotion.id}
                                        data-test-selector="orderLineCardExpandedPromotionName"
                                    >
                                        {promotion.name}
                                    </Typography>
                                ))}
                            </GridItem>
                            <GridItem {...styles.quantityGridItem}>
                                <CartLineQuantity cart={cart} editable={editable} extendedStyles={styles.quantity} />
                            </GridItem>
                            <GridItem {...styles.extendedUnitNetPriceGridItem}>
                                {cartLine.pricing && (showPricing || isCheckoutReviewAndSubmitPage) && (
                                    <>
                                        {enableVat && vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                            <>
                                                <SmallHeadingAndText
                                                    heading={`${translate("Subtotal")} (${translate("Ex. VAT")})`}
                                                    text={cartLine.pricing.extendedUnitNetPriceDisplay}
                                                    extendedStyles={styles.subtotalWithoutVatText}
                                                />
                                                <SmallHeadingAndText
                                                    heading={`${translate("Total VAT")} (${cartLine.pricing.vatRate}%)`}
                                                    text={cartLine.pricing.vatAmountDisplay}
                                                    extendedStyles={styles.vatAmount}
                                                />
                                            </>
                                        )}
                                        <SmallHeadingAndText
                                            heading={
                                                !enableVat
                                                    ? translate("Subtotal")
                                                    : vatPriceDisplay !== "DisplayWithoutVat"
                                                    ? `${translate("Subtotal")} (${translate("Inc. VAT")})`
                                                    : `${translate("Subtotal")} (${translate("Ex. VAT")})`
                                            }
                                            text={
                                                enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                                    ? cartLine.pricing.extendedUnitRegularPriceWithVatDisplay
                                                    : cartLine.pricing.extendedUnitNetPriceDisplay
                                            }
                                            extendedStyles={styles.extendedUnitNetPrice}
                                        />
                                    </>
                                )}
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                    {/* Carstock Section */}
                    {isEnabledCarstock !== "false" && isBroker && isOrderInCart && isCarStockItem && (
                        <GridItem {...styles.productMarkAsFreeGridItem}>
                            <GridContainer {...styles.MarkAsFreeButtonContainer}>
                                <GridItem {...styles.MarkAsFreeButtonGridItem}>
                                    {!isMarkFree && (
                                        <Button onClick={() => onHandleMarkAsFreeButton()} {...styles.markAsFreeButton}>
                                            {siteMessage("Mark_Free")}
                                        </Button>
                                    )}

                                    <Modal
                                        headline={translate("Choose a Reason Code")}
                                        {...styles.selectReasonModal}
                                        isOpen={isShowReasonModal}
                                        handleClose={() => onHandleCloseModal()}
                                        data-test-selector="BudgetModal"
                                    >
                                        {isReasonError && (
                                            <Typography color="red"> Please choose reason code.</Typography>
                                        )}

                                        <RadioGroup
                                            {...styles.reasonListOptionRadioGroup}
                                            value={reasonListOption}
                                            onChangeHandler={reasonListOptionChangeHandler}
                                            data-test-selector="resonListOption"
                                        >
                                            {options.map(item => (
                                                <Radio key={item} value={item}>
                                                    {item}
                                                </Radio>
                                            ))}
                                        </RadioGroup>
                                        <StyledWrapper {...styles.reasonButtonWarpper}>
                                            <Button onClick={() => onHandleCloseModal()} {...styles.cancelButton}>
                                                {translate("Cancel")}
                                            </Button>
                                            <Button onClick={() => submitMarkFreeHandler()} {...styles.saveButton}>
                                                {translate("Save")}
                                            </Button>
                                        </StyledWrapper>
                                    </Modal>
                                </GridItem>
                                <GridItem {...styles.MarkAsFreeDropDownGridItem}>
                                    {isMarkFree && (
                                        <StyledWrapper {...styles.unmarkButtonWarpper}>
                                            <Typography>
                                                <b>Reason: </b>
                                            </Typography>
                                            <Typography>{reasonListOption}</Typography>
                                            <Button
                                                onClick={() => onHandleUnMarkAsFreeButton()}
                                                {...styles.unmarkAsFreeButton}
                                            >
                                                {siteMessage("Unmark_As_Free")}
                                            </Button>
                                        </StyledWrapper>
                                    )}
                                </GridItem>
                            </GridContainer>
                        </GridItem>
                    )}
                    {/* Sample Product */}
                    {/* {isEnabledCarstock !== "false" && isOrderInCart && productQtyAllocatedToUser && (
                        <GridItem {...styles.productMarkAsFreeGridItem}>
                            <SampleProductBudget
                                qty={invoicedQty || "0"}
                                totalQty={productQtyAllocatedToUser || "0"}
                            />
                        </GridItem>
                    )} */}

                    {/* {cartLine.notes && (
                        <GridItem {...styles.orderLineNotesGridItem}>
                            <CartLineNotes
                                cart={cart}
                                editable={editable}
                                label={translate("Line Notes")}
                                extendedStyles={styles.cartLineNotes}
                            />
                        </GridItem>
                    )} */}
                    {cart.showCostCode && cartLine.costCode && (
                        <GridItem {...styles.costCodeGridItem}>
                            <Typography {...styles.costCodeValueText} as="p">
                                <Typography {...styles.costCodeLabelText}>{cart.costCodeLabel}:</Typography>
                                {cartLine.costCode}
                            </Typography>
                        </GridItem>
                    )}
                </GridContainer>
            </GridItem>
        </GridContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withCartLine(CartLineCardExpanded));
